import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CallOffMechanism, Currency, useRegisterFrameworkAgreementMutation } from "../../autogen/bff-api";
import { useRunsPublicProcurements } from "../../common/organization/useRunsPublicProcurements";
import { FrameworkRegulationFields, isComplete, type FrameworkRegulation } from "./FrameworkRegulationFields";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { organizationHasSeveralDepartments } from "../../common/departments/departmentVisibility";
import {
  contractDepartmentOptions,
  initialContractDepartmentId,
} from "../../common/departments/eventCreationDepartments";
import { useApiError } from "../../common/errors/useApiError";
import { NumericInput } from "../../common/input/NumericInput";
import { visibleLength } from "../../common/text/visibleLength";
import { urls } from "../../urls";

const MECHANISMS = [
  CallOffMechanism.DirectAward,
  CallOffMechanism.MiniCompetition,
  CallOffMechanism.DirectAwardAndMiniCompetition,
];

export const RegisterFrameworkAgreementModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayer = useApiError();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const [register, { isLoading }] = useRegisterFrameworkAgreementMutation();

  const departments = contractDepartmentOptions(selectedOrg);
  const showsPicker = organizationHasSeveralDepartments(selectedOrg);
  const [departmentId, setDepartmentId] = useState(() => initialContractDepartmentId(selectedOrg));
  const [title, setTitle] = useState("");
  const [callOffMechanism, setMechanism] = useState(CallOffMechanism.DirectAwardAndMiniCompetition);
  const [ceilingValue, setCeilingValue] = useState<number | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [maxParticipants, setMaxParticipants] = useState<number | null>(null);
  const runsPublicProcurements = useRunsPublicProcurements();
  const [letUnder, setLetUnder] = useState<FrameworkRegulation>({ regulation: null, foaPart: null });

  const endsBeforeStart = startDate !== "" && endDate !== "" && endDate < startDate;
  const ceilingNotPositive = ceilingValue !== null && ceilingValue <= 0;
  const maxParticipantsNotPositive = maxParticipants !== null && maxParticipants < 1;
  const blockedReason = !departmentId
    ? t("Select department")
    : visibleLength(title) === 0
      ? t("Write a title")
      : runsPublicProcurements && !isComplete(letUnder)
        ? t("Choose the regulation it was let under")
        : null;
  const canSubmit = blockedReason === null && !endsBeforeStart && !ceilingNotPositive && !maxParticipantsNotPositive;

  const submit = async () => {
    if (!departmentId) return;
    try {
      const framework = await register({
        registerFrameworkAgreementRequest: {
          departmentId,
          title: title.trim(),
          callOffMechanism,
          ceilingValue: ceilingValue === null ? undefined : { value: ceilingValue, currency: Currency.Nok },
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          maxParticipants: maxParticipants ?? undefined,
          regulation: runsPublicProcurements ? letUnder.regulation ?? undefined : undefined,
          foaPart: runsPublicProcurements ? letUnder.foaPart ?? undefined : undefined,
        },
      }).unwrap();
      onClose();
      navigate(urls.app.frameworkAgreements.view.go(framework.id));
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="smModalBackground">
        <ModalHeader>{t("Register framework agreement")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Text color="smMuted" fontSize="sm">
              {t(
                "For a framework agreement concluded outside SourceMagnet. You add its suppliers on the agreement's page.",
              )}
            </Text>
            <FormControl isRequired>
              <FormLabel htmlFor="register-framework-title">{t("Title")}</FormLabel>
              <Input
                id="register-framework-title"
                backgroundColor="smBackground"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormControl>
            {showsPicker && (
              <FormControl isRequired>
                <FormLabel htmlFor="register-framework-department">{t("Department")}</FormLabel>
                <Select
                  id="register-framework-department"
                  backgroundColor="smBackground"
                  value={departmentId ?? ""}
                  placeholder={t("Select department") ?? undefined}
                  onChange={(e) => setDepartmentId(e.target.value || undefined)}
                >
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText>{t("The framework agreement belongs to this department")}</FormHelperText>
              </FormControl>
            )}
            {runsPublicProcurements && (
              <FrameworkRegulationFields idPrefix="register-framework" value={letUnder} onChange={setLetUnder} isRequired />
            )}
            <FormControl>
              <FormLabel htmlFor="register-framework-mechanism">{t("Call-off mechanism")}</FormLabel>
              <Select
                id="register-framework-mechanism"
                backgroundColor="smBackground"
                value={callOffMechanism}
                onChange={(e) => setMechanism(e.target.value as CallOffMechanism)}
              >
                {MECHANISMS.map((value) => (
                  <option key={value} value={value}>
                    {t(value)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isInvalid={ceilingNotPositive}>
              <FormLabel htmlFor="register-framework-ceiling">{t("Ceiling value")}</FormLabel>
              <InputGroup maxW="280px">
                <NumericInput
                  id="register-framework-ceiling"
                  backgroundColor="smBackground"
                  numericValue={ceilingValue}
                  placeholder="0"
                  onNumericChange={setCeilingValue}
                />
                <InputRightAddon>{Currency.Nok}</InputRightAddon>
              </InputGroup>
              {ceilingNotPositive ? (
                <FormErrorMessage>{t("Must be greater than zero")}</FormErrorMessage>
              ) : (
                <FormHelperText>{t("Excluding VAT")}</FormHelperText>
              )}
            </FormControl>
            <HStack align="start" spacing="4">
              <FormControl>
                <FormLabel htmlFor="register-framework-start">{t("Start date")}</FormLabel>
                <Input
                  id="register-framework-start"
                  type="date"
                  backgroundColor="smBackground"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormControl>
              <FormControl isInvalid={endsBeforeStart}>
                <FormLabel htmlFor="register-framework-end">{t("End date")}</FormLabel>
                <Input
                  id="register-framework-end"
                  type="date"
                  backgroundColor="smBackground"
                  min={startDate || undefined}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <FormErrorMessage>{t("The end date is before the start date")}</FormErrorMessage>
              </FormControl>
            </HStack>
            <FormControl isInvalid={maxParticipantsNotPositive}>
              <FormLabel htmlFor="register-framework-max">{t("Maximum number of suppliers")}</FormLabel>
              <NumericInput
                id="register-framework-max"
                maxW="140px"
                backgroundColor="smBackground"
                numericValue={maxParticipants}
                onNumericChange={(value) => setMaxParticipants(value === null ? null : Math.floor(value))}
              />
              {maxParticipantsNotPositive ? (
                <FormErrorMessage>{t("Must be greater than zero")}</FormErrorMessage>
              ) : (
                <FormHelperText>{t("Leave empty if the agreement sets no limit")}</FormHelperText>
              )}
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          {blockedReason && (
            <Text fontSize="sm" color="smMuted" mr="auto">
              {blockedReason}
            </Text>
          )}
          <Button variant="ghost" mr="3" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" isLoading={isLoading} isDisabled={!canSubmit} onClick={submit}>
            {t("Register framework agreement")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
